import React, { useEffect, useState} from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentUser, signOut, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';

import '@aws-amplify/ui-react/styles.css';
import './App.css';
import config from './config';

const App = ({ testMode = false }) => {

  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUser = async () => {
      try {
          const user = await getCurrentUser();
          console.log('Current user:', user);
          if (!user) {
              navigate('/login');
          }
          const attributes = await fetchUserAttributes(user);
          console.log('User attributes fetched successfully');
          console.log('User attributes:', attributes);
          setUser(attributes);
      } catch (error) {
          console.error('Error fetching user:', error);
          navigate('/login');
      }
    }

    checkUser();
  }, []);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) return;
        const res = await fetch(`${config.backendUrl}/api/admin/metrics/conversion?days=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setMetrics(await res.json());
      } catch (error) {
        console.error('Failed to load metrics:', error);
        setMetricsError(error.message);
      }
    };

    loadMetrics();
  }, []);
  
  const handleSignOut = async () => {
    try {
        await signOut();
        window.location.reload(); // Reload to ensure the user is redirected to the login page
    } catch (error) {
        console.error('Error signing out:', error);
    }
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="header-left">
            <button className="mobile-menu-toggle" onClick={toggleSidebar}>
              ☰
            </button>
            <Link to="/" className="header-logo">
              Admin Dashboard
            </Link>
          </div>
          
          <div className="header-right">
            <div className="header-user">
              <span>👤</span>
              <span>{user && user.given_name || 'User'}</span>
              {testMode && <span style={{color: 'var(--warning-color)'}}> (Test)</span>}
            </div>
            <button className="btn btn-secondary" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="main-layout">
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h3 className="sidebar-title">Navigation</h3>
          </div>
          
          <nav className="sidebar-nav">
            <div className="sidebar-nav-group">
              <div className="sidebar-nav-group-title">Main</div>
              <ul className="sidebar-nav-list">
                <li className="sidebar-nav-item">
                  <Link 
                    to={testMode ? "/test/orders" : "/orders"} 
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">📋</span>
                    View Orders
                  </Link>
                </li>
                <li className="sidebar-nav-item">
                  <Link 
                    to={"/links"} 
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">🔗</span>
                    Builder Links
                  </Link>
                </li>
                <li className="sidebar-nav-item">
                  <Link 
                    to={testMode ? "/test/products" : "/products"} 
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">📦</span>
                    Manage Products
                  </Link>
                </li>
                <li className="sidebar-nav-item">
                  <Link 
                    to={testMode ? "/test/users" : "/users"} 
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">👥</span>
                    Users
                  </Link>
                </li>
                <li className="sidebar-nav-item">
                  <Link
                    to={testMode ? "/test/puppy-generator" : "/puppy-generator"}
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">🐶</span>
                    Puppy Generator
                  </Link>
                </li>
                <li className="sidebar-nav-item">
                  <Link
                    to={testMode ? "/test/caching-system" : "/caching-system"}
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">🗂️</span>
                    Caching System
                  </Link>
                </li>
                <li className="sidebar-nav-item">
                  <Link
                    to={testMode ? "/test/settings" : "/settings"}
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">⚙️</span>
                    Settings
                  </Link>
                </li>
              </ul>
            </div>
            
            <div className="sidebar-nav-group">
              <div className="sidebar-nav-group-title">System</div>
              <ul className="sidebar-nav-list">
                <li className="sidebar-nav-item">
                  <Link 
                    to={testMode ? "/" : "/test"} 
                    className="sidebar-nav-link"
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-nav-icon">🔄</span>
                    Toggle Mode
                  </Link>
                </li>
              </ul>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          <div className="main-content-header">
            <h1 className="main-content-title">
              Welcome back, {user && user.given_name || 'Admin'}!
            </h1>
            <p className="main-content-subtitle">
              Here's what's happening with your store today.
            </p>
          </div>

          {/* Dashboard Cards */}
          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Total Orders</h3>
                <span>📈</span>
              </div>
              <p className="dashboard-card-value">0</p>
              <div className="dashboard-card-change">
                <span>No change</span>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Total Products</h3>
                <span>📦</span>
              </div>
              <p className="dashboard-card-value">0</p>
              <div className="dashboard-card-change">
                <span>No change</span>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Active Users</h3>
                <span>👥</span>
              </div>
              <p className="dashboard-card-value">1</p>
              <div className="dashboard-card-change positive">
                <span>↗</span>
                <span>You're online</span>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">System Status</h3>
                <span>🟢</span>
              </div>
              <p className="dashboard-card-value">Online</p>
              <div className="dashboard-card-change positive">
                <span>All systems operational</span>
              </div>
            </div>
          </div>

          {/* Conversion metrics */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Look to the Sky — Preview → Order Conversion (last 30 days)</h3>
            </div>
            <div className="card-body">
              {metricsError && (
                <p style={{ color: 'var(--danger-color, #c0392b)' }}>
                  Couldn't load metrics: {metricsError}
                </p>
              )}
              {!metrics && !metricsError && <p>Loading metrics…</p>}
              {metrics && (
                <>
                <p style={{ marginTop: 0, marginBottom: 'var(--spacing-md)', fontSize: '0.85rem', opacity: 0.7 }}>
                  {metrics.previews_generated === 0
                    ? 'No previews tracked yet — counts begin as previews are generated.'
                    : `Based on ${metrics.previews_generated} preview${metrics.previews_generated === 1 ? '' : 's'} and ${metrics.orders} order${metrics.orders === 1 ? '' : 's'} since ${new Date(metrics.tracking_since * 1000).toLocaleDateString()}.${metrics.low_sample ? ' Small sample — treat the rate as preliminary.' : ''}`}
                </p>
                <div className="dashboard-grid">
                  <div className="dashboard-card">
                    <div className="dashboard-card-header">
                      <h3 className="dashboard-card-title">Previews generated</h3>
                      <span>🖼️</span>
                    </div>
                    <p className="dashboard-card-value">{metrics.previews_generated}</p>
                    <div className="dashboard-card-change">
                      <span>Exact server-side count</span>
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-header">
                      <h3 className="dashboard-card-title">Orders</h3>
                      <span>🛒</span>
                    </div>
                    <p className="dashboard-card-value">{metrics.orders}</p>
                    <div className="dashboard-card-change">
                      <span>{metrics.mode} mode</span>
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-header">
                      <h3 className="dashboard-card-title">Preview → order rate</h3>
                      <span>📈</span>
                    </div>
                    <p className="dashboard-card-value">
                      {metrics.preview_to_order_rate_pct != null
                        ? `${metrics.preview_to_order_rate_pct}%`
                        : '—'}
                    </p>
                    <div className="dashboard-card-change">
                      <span>
                        {metrics.previews_generated === 0
                          ? 'No previews logged yet'
                          : metrics.low_sample
                            ? 'Preliminary — low sample'
                            : 'orders ÷ previews'}
                      </span>
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-header">
                      <h3 className="dashboard-card-title">GA visitors → order</h3>
                      <span>🌐</span>
                    </div>
                    <p className="dashboard-card-value">
                      {metrics.ga_unique_preview_visitors != null
                        ? `${metrics.ga_visitor_to_order_rate_pct ?? '—'}%`
                        : '—'}
                    </p>
                    <div className="dashboard-card-change">
                      <span>
                        {metrics.ga_unique_preview_visitors != null
                          ? `${metrics.ga_unique_preview_visitors} unique visitors`
                          : 'GA not connected yet'}
                      </span>
                    </div>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Quick Actions</h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap' }}>
                <Link to={testMode ? "/test/orders" : "/orders"} className="btn btn-primary">
                  View Orders
                </Link>
                <Link to={testMode ? "/test/products" : "/products"} className="btn btn-secondary">
                  Manage Products
                </Link>
                <Link to={testMode ? "/test/users" : "/users"} className="btn btn-secondary">
                  View Users
                </Link>
                <Link to={testMode ? "/test/settings" : "/settings"} className="btn btn-ghost">
                  Settings
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay open" onClick={closeSidebar}></div>
      )}
    </div>
  );
};

export const Settings = ({ goBack }) => {
  return (
    <div className="main-content">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">Settings</h1>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={goBack}>
            ← Go back
          </button>
        </div>
      </div>
      
      <div className="card">
        <div className="card-body">
          <p>Settings page content will go here.</p>
        </div>
      </div>
    </div>
  );
}

export default App;